/**
 * @file appUrl.ts
 * @description The one URL scheme for every entry point (ADR-128, DL-520): `/?a={app}&p={programId}&s={slug}`.
 * Short keys keep the printed Donor Tag QR small and give every portal the same shape. There are no path entry
 * points and no long-form aliases (`app`, `db`, `dbId`). This file is the single place the keys and app codes are
 * defined; it is shared by the SPA and the Worker. Code elsewhere keeps the long app names (`'admin'`, …) — they
 * are converted only here, at the URL boundary.
 */

export type AppName = 'admin' | 'ops' | 'household' | 'apply' | 'provision' | 'tree' | 'tag';
export type TagViewApp = 'tree' | 'tag';

/** Param keys: `a` = app, `p` = Program id, `s` = tag slug (or Tag_ID). */
export const URL_KEY = { app: 'a', program: 'p', slug: 's' } as const;

const APP_TO_CODE: Record<AppName, string> = {
	admin: 'a',
	ops: 'o',
	household: 'h',
	apply: 'y',
	provision: 'p',
	tree: 't',
	// Legacy (DL-618): accepted so already-printed QR codes keep opening their tag, never generated. A bare `a=g`
	// (no slug) shows the tree, same as `a=t`; there is no tag-number search.
	tag: 'g'
};
const CODE_TO_APP: Record<string, AppName> = Object.fromEntries(
	Object.entries(APP_TO_CODE).map(([app, code]) => [code, app as AppName])
);

export interface AppUrlParts {
	app: AppName;
	/** Omitted for `provision`, which runs before any Program exists. */
	programId?: string | null;
	/** Only the tree/tag views carry a slug; omitted for the tree browse view. */
	slug?: string | null;
}

/** Extra query params (`mode`, `campaign`, …) — null/undefined/empty values are dropped. */
export type ExtraParams = Record<string, string | null | undefined>;

/** Query params for `setSearchParams` / URL building. */
export function appUrlParams({ app, programId, slug }: AppUrlParts, extra: ExtraParams = {}): Record<string, string> {
	const params: Record<string, string> = { [URL_KEY.app]: APP_TO_CODE[app] };
	if (programId) params[URL_KEY.program] = programId;
	if (slug) params[URL_KEY.slug] = slug;
	for (const [key, value] of Object.entries(extra)) {
		if (value) params[key] = value;
	}
	return params;
}

/** `origin` + `/?…`; pass an empty string for a root-relative URL (`window.location.href = …`). */
export function buildAppUrl(origin: string, parts: AppUrlParts, extra: ExtraParams = {}): string {
	return `${origin}/?${new URLSearchParams(appUrlParams(parts, extra)).toString()}`;
}

/** The one real route besides the portal root: `/auth/login`, carrying the same keys (`origin` may be empty). */
export function buildLoginUrl(origin: string, parts: AppUrlParts, extra: ExtraParams = {}): string {
	return `${origin}/auth/login?${new URLSearchParams(appUrlParams(parts, extra)).toString()}`;
}

/** The Worker's Google sign-in start URL (`/auth/google`) — same keys, so the Worker reads them with `readApp`/`readProgramId`. */
export function buildGoogleAuthUrl(apiUrl: string, parts: AppUrlParts, extra: ExtraParams = {}): string {
	return `${apiUrl}/auth/google?${new URLSearchParams(appUrlParams(parts, extra)).toString()}`;
}

/** The app named by the `a` key, or null when it is absent or not a known code. */
export function readApp(params: URLSearchParams): AppName | null {
	return CODE_TO_APP[params.get(URL_KEY.app) ?? ''] ?? null;
}

export function readProgramId(params: URLSearchParams): string | null {
	return params.get(URL_KEY.program);
}

export function readSlug(params: URLSearchParams): string | null {
	return params.get(URL_KEY.slug);
}

export function isTagViewApp(app: AppName | null): app is TagViewApp {
	return app === 'tree' || app === 'tag';
}
